<template>
  <div>
    <!-- Page Header -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h1 class="page-title mb-0">Domain Detection Summary</h1>
      <div class="d-flex align-items-center" style="gap: 8px">
        <!-- <div v-if="latestUpdate" class="d-flex align-items-center" style="gap: 6px">
          <span style="font-size: 14px; font-weight: 500; color: #4b4b4b; white-space: nowrap">Latest Update: {{
            latestUpdate }}</span>
        </div> -->
      </div>
    </div>

    <!-- Filter Bar -->
    <div class="filter-bar mb-4">
      <div class="filter-bar__item">
        <domain-checkbox-dropdown ref="refDomain" v-model="filter.domain" :options="filterOptions.domains"
          :max-select="3" placeholder="All Domains" @open="closeOtherDropdowns('refDomain')" />
      </div>
      <div class="filter-bar__item">
        <single-select-dropdown ref="refProvince" v-model="filter.province" :options="filterOptions.provinces"
          placeholder="All Provinsi" title="Select Provinsi" @open="closeOtherDropdowns('refProvince')" />
      </div>
      <div class="filter-bar__item">
        <single-select-dropdown ref="refRegion" v-model="filter.region" :options="filterOptions.regions"
          placeholder="All Region" title="Select Region"
          :empty-message="filter.province ? 'No results' : 'Pilih provinsi terlebih dahulu'"
          @open="closeOtherDropdowns('refRegion')" />
      </div>
      <div class="filter-bar__item">
        <single-select-dropdown ref="refDistrict" v-model="filter.district" :options="filterOptions.districts"
          placeholder="All District" title="Select District"
          :empty-message="filter.region ? 'No results' : 'Pilih Kabupaten/Kota terlebih dahulu'"
          @open="closeOtherDropdowns('refDistrict')" />
      </div>
      <!-- <div class="filter-bar__item">
        <single-select-dropdown ref="refRadius" v-model="filter.radius" :options="filterOptions.radii"
          placeholder="Radius" title="Select Radius" @open="closeOtherDropdowns('refRadius')" />
      </div> -->
      <button class="btn-reset-filter" :class="{ 'btn-reset-filter--active': isFilterActive }" @click="resetFilter">
        <i class="fas fa-undo" style="font-size: 12px"></i>
        <span>Reset Filter</span>
      </button>
    </div>

    <div class="d-flex mb-4" style="gap: 16px">
      <!-- Total Scans -->
      <div class="kpi-wrap">
        <domain-summary-stats-card title="Total Scans" :value="summary.totalScans" icon-bg-color="#dcefff">
          <template #icon>
            <img :src="require('@/assets/icons/domain-detection/total-scan.svg')" width="33" height="33" />
          </template>
        </domain-summary-stats-card>
        <button type="button" class="kpi-wrap__info" id="kpi-info-scans" aria-label="Info Total Scans"><i class="fal fa-info-circle"></i></button>
        <b-popover target="kpi-info-scans" triggers="hover focus" placement="top" boundary="viewport" :fallback-placement="['top']">{{ ignoredTooltip }}</b-popover>
        <div class="kpi-wrap__sub">&nbsp;</div>
      </div>

      <!-- Suspicious Domains -->
      <div class="kpi-wrap">
        <domain-summary-stats-card title="Suspicious Domains" :value="summary.suspiciousDomains"
          icon-bg-color="#ffe6e6">
          <template #icon>
            <img :src="require('@/assets/icons/domain-detection/suspicious-domain.svg')" width="33" height="33" />
          </template>
        </domain-summary-stats-card>
        <button type="button" class="kpi-wrap__info" id="kpi-info-susp" aria-label="Info Suspicious Domains"><i class="fal fa-info-circle"></i></button>
        <b-popover target="kpi-info-susp" triggers="hover focus" placement="top" boundary="viewport" :fallback-placement="['top']">{{ ignoredTooltip }}</b-popover>
        <div class="kpi-wrap__sub">Excludes {{ summary.ignored }} ignored</div>
      </div>

      <!-- Total District -->
      <div class="kpi-wrap">
        <domain-summary-stats-card title="Total District" :value="summary.location" icon-bg-color="#fff3e2">
          <template #icon>
            <img :src="require('@/assets/icons/domain-detection/total-district.svg')" width="33" height="33" />
          </template>
        </domain-summary-stats-card>
        <button type="button" class="kpi-wrap__info" id="kpi-info-district" aria-label="Info Total District"><i class="fal fa-info-circle"></i></button>
        <b-popover target="kpi-info-district" triggers="hover focus" placement="top" boundary="viewport" :fallback-placement="['top']">{{ ignoredTooltip }}</b-popover>
        <div class="kpi-wrap__sub">&nbsp;</div>
      </div>

      <!-- Total Dealer Apps (tidak termasuk card bertooltip di PRD) -->
      <div class="kpi-wrap">
        <domain-summary-stats-card title="Total Dealer Apps" :value="summary.dealer" icon-bg-color="#f9efff">
          <template #icon>
            <img :src="require('@/assets/icons/domain-detection/total-dealer-apps.svg')" width="33" height="33" />
          </template>
        </domain-summary-stats-card>
        <div class="kpi-wrap__sub">&nbsp;</div>
      </div>
    </div>

    <!-- Map + Insights -->
    <div class="d-flex mb-4" style="gap: 16px;">
      <div
        style="flex: 0 0 67%; min-width: 0; border-radius: 8px; overflow: hidden; box-shadow: 0 0 3px rgba(0, 0, 0, 0.16)">
        <domain-summary-heatmap :data="mapData"></domain-summary-heatmap>
      </div>
      <div style="flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 16px">
        <domain-summary-suspicious-donut :domains="suspiciousDomainsPie"></domain-summary-suspicious-donut>
        <!-- :key diubah setelah Ignore supaya Top 5 District mengambil data ulang (exclude domain Ignored) -->
        <domain-summary-top5-dealer :key="top5DistrictKey" @detail="onDistrictDetail"></domain-summary-top5-dealer>
      </div>
    </div>

    <!-- Top 5 Suspicious Domains Table -->
    <domain-summary-top5-table ref="refTop5Table" @watchlist="onWatchlist" @ignore="onIgnore" @detail="onDetail"
      @viewAll="onViewAll"></domain-summary-top5-table>

    <!-- Watchlist Confirmation Modal -->
    <b-modal v-model="watchlistModal.show" centered size="sm" hide-header hide-footer>
      <div class="watchlist-modal">
        <div class="watchlist-modal__icon">
          <img :src="require('@/assets/icons/domain-detection/watchlist-warning.svg')" width="109" height="109" />
        </div>
        <div class="watchlist-modal__body">
          <p class="watchlist-modal__title">Watchlist This Domain?</p>
          <p class="watchlist-modal__subtitle">This domain's status will be changed to Watchlisted for ongoing
            monitoring.
          </p>
        </div>
        <div class="watchlist-modal__footer">
          <button class="watchlist-modal__btn watchlist-modal__btn--cancel" @click="watchlistModalHide">Cancel</button>
          <button class="watchlist-modal__btn watchlist-modal__btn--confirm" :disabled="watchlistModal.loading"
            @click="watchlistConfirm">
            <span v-if="watchlistModal.loading">
              <b-spinner small></b-spinner>
            </span>
            <span v-else>Add to Watchlist</span>
          </button>
        </div>
      </div>
    </b-modal>

    <!-- Watchlist Success Modal -->
    <b-modal v-model="successModal.show" centered size="sm" hide-header hide-footer>
      <div class="watchlist-modal">
        <div class="watchlist-modal__icon">
          <img :src="require('@/assets/icons/domain-detection/watchlist-success.svg')" width="134" height="134" />
        </div>
        <div class="watchlist-modal__body">
          <p class="watchlist-modal__title">Successfully Watchlisted Domain</p>
          <p class="watchlist-modal__subtitle">Domain successfully added to watchlist</p>
        </div>
        <div class="watchlist-modal__footer">
          <!-- <button
            class="watchlist-modal__btn watchlist-modal__btn--cancel"
            @click="watchlistSuccessGoToReview"
          >Go to Domain Review</button> -->
          <button class="watchlist-modal__btn watchlist-modal__btn--confirm"
            @click="successModal.show = false">OK</button>
        </div>
      </div>
    </b-modal>

    <!-- Ignore Modal (shared component) -->
    <ignore-domain-modal v-model="ignoreModal.show" mode="ignore" :domain-name="ignoreName"
      :risk-score="ignoreRisk" :is-public-platform="ignorePublic" :loading="ignoreModal.loading"
      @confirm="ignoreConfirm" />
  </div>
</template>

<script>
import qs from "qs";
import customFilter from "@/components/filter/customFilter";
import DomainCheckboxDropdown from "./children/DomainCheckboxDropdown.vue";
import SingleSelectDropdown from "./children/SingleSelectDropdown.vue";
import DomainSummaryStatsCard from "./children/DomainSummaryStatsCard.vue";
import DomainSummaryHeatmap from "./children/DomainSummaryHeatmap.vue";
import DomainSummarySuspiciousDonut from "./children/DomainSummarySuspiciousDonut.vue";
import DomainSummaryTop5Dealer from "./children/DomainSummaryTop5Dealer.vue";
import DomainSummaryTop5Table from "./children/DomainSummaryTop5Table.vue";
import IgnoreDomainModal from "./children/IgnoreDomainModal.vue";

export default {
  name: "DomainDetectionSummary",
  metaInfo: { title: "Domain Detection Summary" },
  components: {
    customFilter,
    DomainCheckboxDropdown,
    SingleSelectDropdown,
    DomainSummaryStatsCard,
    DomainSummaryHeatmap,
    DomainSummarySuspiciousDonut,
    DomainSummaryTop5Dealer,
    DomainSummaryTop5Table,
    IgnoreDomainModal,
  },
  data() {
    return {
      latestUpdate: null,
      filter: {
        start: null,
        end: null,
        filter: null,
        domain: [],
        province: null,
        region: null,
        district: null,
        radius: null,
      },
      filterOptions: {
        domains: [],
        provinces: [],
        regions: [],
        districts: [],
        radii: [],
      },
      summary: {
        totalScans: 0,
        suspiciousDomains: 0,
        location: 0,
        dealer: 0,
        ignored: 0,
      },
      mapData: [],
      suspiciousDomainsPie: [],
      top5DistrictKey: 0,
      ignoredTooltip:
        'Excludes domains marked as Ignored and all of their scans. Ignored domains can be viewed in Review Domain using the "Ignored" status filter.',
      watchlistModal: {
        show: false,
        item: null,
        loading: false,
      },
      successModal: {
        show: false,
      },
      ignoreModal: {
        show: false,
        item: null,
        loading: false,
      },
    };
  },
  mounted() {
    this.fetchProvinces();
    this.fetchDomains();
    this.fetchOverview();
    this.fetchSuspiciousDonutChart();
    this.fetchMapData();
    this.fetchLatestUpdate();
  },
  computed: {
    domainParam() {
      return this.filter.domain.length ? this.filter.domain.map((d) => d.value).join(",") : null;
    },
    isFilterActive() {
      return this.filter.domain.length > 0 || this.filter.province !== null || this.filter.region !== null || this.filter.district !== null || this.filter.radius !== null;
    },
    ignoreName() {
      const i = this.ignoreModal.item;
      return i ? i.domain || i.domainUrl || i.domain_url || "" : "";
    },
    ignoreRisk() {
      const i = this.ignoreModal.item;
      return i ? i.riskScore || i.risk_score || "High" : "";
    },
    ignorePublic() {
      const i = this.ignoreModal.item;
      return !!(i && (i.isPublicPlatform || i.is_public_platform));
    },
  },
  watch: {
    "filter.domain"() {
      this.fetchData();
    },
    "filter.province"(val) {
      this.filter.region = null;
      this.filter.district = null;
      this.filterOptions.regions = [];
      this.filterOptions.districts = [];
      if (val) this.fetchRegions(val.value);
      this.fetchData();
    },
    "filter.region"(val) {
      this.filter.district = null;
      this.filterOptions.districts = [];
      if (val) this.fetchDistricts(val.value);
      this.fetchData();
    },
    "filter.district"() {
      this.fetchData();
    },
  },
  methods: {
    closeOtherDropdowns(exceptRef) {
      const allRefs = ["refDomain", "refProvince", "refRegion", "refDistrict", "refRadius"];
      allRefs.forEach((name) => {
        if (name !== exceptRef && this.$refs[name]) this.$refs[name].close();
      });
    },
    async fetchProvinces() {
      const resp = await this.$http.get("/api/master/provinsi");
      this.filterOptions.provinces = resp.data.data.map((p) => ({
        name: p.nama_provinsi,
        value: p.kode_provinsi,
      }));
    },
    async fetchRegions(kodeProvinsi) {
      const resp = await this.$http.get(`/api/master/kotakabupaten?kode_provinsi=${kodeProvinsi}`);
      this.filterOptions.regions = resp.data.data.map((r) => ({
        name: r.nama_kotakab,
        value: r.kode_kotakab,
      }));
    },
    async fetchDistricts(kodeKotakabupaten) {
      const resp = await this.$http.get(`/api/master/kecamatan?kode_kotakabupaten=${kodeKotakabupaten}`);
      this.filterOptions.districts = resp.data.data.map((d) => ({
        name: d.nama_kecamatan,
        value: d.kode_kecamatan,
      }));
    },
    async fetchDomains() {
      // status=all = Under Review + Watchlisted, jadi domain Ignored tidak muncul di dropdown (UR-06 AC#7).
      const params = qs.stringify(
        { page: 1, limit: 9999, status: "all", search_domain: "" },
        { skipNulls: false },
      );
      const resp = await this.$http.get(`/api/domain-detection-review-domain/table?${params}`);
      const rows = (resp.data.data || {}).data || [];
      this.filterOptions.domains = rows.map((d) => ({
        name: d.domain_url,
        value: d.domain_id,
      }));
    },
    changeFilter(filter) {
      for (var i in filter) this.filter[i] = filter[i];
      this.fetchData();
    },
    fetchData() {
      this.fetchOverview();
      this.fetchSuspiciousDonutChart();
      this.fetchMapData();
    },
    async fetchSuspiciousDonutChart() {
      const resp = await this.$http.get(`/api/domain-detection-summary/top-5-suspicious-domain-chart`);
      this.suspiciousDomainsPie = resp.data.data.map((d) => ({
        domainId: d.domain_id,
        name: d.domain,
        count: d.total,
        percent: d.percent,
      }));
    },
    async fetchOverview() {
      const params = qs.stringify(
        {
          start_date: this.filter.start,
          end_date: this.filter.end,
          domain: this.domainParam,
          province: this.filter.province ? this.filter.province.value : null,
          region: this.filter.region ? this.filter.region.value : null,
          district: this.filter.district ? this.filter.district.value : null,
        },
        { skipNulls: true },
      );

      const resp = await this.$http.get(`/api/domain-detection-summary/overview?${params}`);
      const d = resp.data.data;
      this.summary = {
        totalScans: d.total_scan,
        suspiciousDomains: d.total_suspicious_domain,
        location: d.total_district,
        dealer: d.total_dealer_apps,
        ignored: d.total_ignored_domain || 0,
      };
    },
    async fetchMapData() {
      const params = qs.stringify(
        {
          start_date: this.filter.start,
          end_date: this.filter.end,
          domain: this.domainParam,
          province: this.filter.province ? this.filter.province.value : null,
          region: this.filter.region ? this.filter.region.value : null,
          district: this.filter.district ? this.filter.district.value : null,
        },
        { skipNulls: true },
      );
      const resp = await this.$http.get(`/api/domain-detection-summary/map?${params}`);
      this.mapData = resp.data.data.filter((m) => m.lat && m.lng);
    },
    formatFirstScanDate(val) {
      if (!val || val === "-") return "-";

      const valStr = String(val).trim();
      if (/^\d{4}-\d{2}-\d{2}\s*\|\s*\d{2}:\d{2}:\d{2}$/.test(valStr)) {
        return valStr;
      }

      const date = new Date(val);
      if (isNaN(date.getTime())) return valStr;

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");

      return `${year}-${month}-${day} | ${hours}:${minutes}:${seconds}`;
    },
    async fetchLatestUpdate() {
      try {
        const resp = await this.$http.get("/api/domain-detection-domain-distribution/domain-list", {
          params: { page: 1, limit: 100 }
        });
        const resData = resp.data && resp.data.data ? resp.data.data : (resp.data || {});
        const rowsData = Array.isArray(resData) ? resData : (resData.data || resData.details || []);

        let latestTime = null;
        let latestRaw = null;

        const topLevelCandidate = (resp.data && (resp.data.latest_update || resp.data.first_scan)) ||
          (resData && (resData.latest_update || resData.first_scan));
        if (topLevelCandidate) {
          const cleanTop = typeof topLevelCandidate === "string" ? topLevelCandidate.replace(" | ", " ") : topLevelCandidate;
          const d = new Date(cleanTop);
          if (!isNaN(d.getTime())) {
            latestTime = d.getTime();
            latestRaw = topLevelCandidate;
          }
        }

        if (Array.isArray(rowsData)) {
          rowsData.forEach((row) => {
            const raw = row.first_scan || row.firstScan || row.created_at || row.date;
            if (raw) {
              const cleanRaw = typeof raw === "string" ? raw.replace(" | ", " ") : raw;
              const d = new Date(cleanRaw);
              if (!isNaN(d.getTime())) {
                const time = d.getTime();
                if (latestTime === null || time > latestTime) {
                  latestTime = time;
                  latestRaw = raw;
                }
              }
            }
          });
        }

        if (latestRaw) {
          this.latestUpdate = this.formatFirstScanDate(latestRaw);
        } else {
          this.latestUpdate = "-";
        }
      } catch (err) {
        if (!this.latestUpdate) this.latestUpdate = "-";
      }
    },
    resetFilter() {
      this.filter = {
        start: null,
        end: null,
        filter: null,
        domain: [],
        province: null,
        region: null,
        district: null,
        radius: null,
      };
      this.filterOptions.regions = [];
      this.filterOptions.districts = [];
    },
    /* ---------- Watchlist (existing) ---------- */
    onWatchlist(item) {
      this.watchlistModal.item = item;
      this.watchlistModal.show = true;
    },
    watchlistModalHide() {
      this.watchlistModal.show = false;
      this.watchlistModal.item = null;
    },
    async watchlistConfirm() {
      this.watchlistModal.loading = true;
      try {
        await this.$http.post("/api/domain-detection-review-domain/update-watchlist", {
          domain_id: this.watchlistModal.item.domainId,
          watchlist: true,
        });
        this.watchlistModal.show = false;
        this.watchlistModal.item = null;
        this.successModal.show = true;
        this.$refs.refTop5Table.fetchData();
      } catch (e) {
        this.errorPrint(e);
      } finally {
        this.watchlistModal.loading = false;
      }
    },
    watchlistSuccessGoToReview() {
      this.successModal.show = false;
      this.$router.push("/dashboard/domain-detection/review-domain");
    },

    /* ---------- Ignore (UR-06) ---------- */
    itemDomainId(item) {
      return item.domainId || item.domain_id;
    },
    onIgnore(item) {
      this.ignoreModal = { show: true, item, loading: false };
    },
    async refreshAfterIgnore(domainId) {
      const hadSelected = this.filter.domain.some((d) => d.value === domainId);
      this.fetchDomains();
      this.$refs.refTop5Table.fetchData();
      this.top5DistrictKey += 1; 
      if (hadSelected) {
        this.filter.domain = this.filter.domain.filter((d) => d.value !== domainId);
      } else {
        this.fetchData();
      }
    },
    async ignoreConfirm(reason) {
      if (this.ignoreModal.loading) return;
      const item = this.ignoreModal.item;
      const domainId = this.itemDomainId(item);
      const name = this.ignoreName;
      this.ignoreModal.loading = true;
      try {
        await this.$http.post("/api/domain-detection-summary/ignore", {
          domain_id: domainId,
          reason: reason || "",
        });
        this.ignoreModal.show = false;
        this.ignoreModal.item = null;
        this.$bvToast.toast("It will not be counted in domain monitoring or sent to Telegram alerts.", {
          title: `${name} has been ignored`,
          variant: "success",
          solid: true,
          autoHideDelay: 4000,
        });
        await this.refreshAfterIgnore(domainId);
      } catch (err) {
        this.$bvToast.toast(
          (err && err.response && err.response.data && err.response.data.message) || "Please try again.",
          {
            title: "Failed to ignore domain. Please try again.",
            variant: "danger",
            solid: true,
            autoHideDelay: 5000,
          },
        );
        if (err && err.response && err.response.status === 400) {
          this.ignoreModal.show = false;
          await this.refreshAfterIgnore(domainId);
        }
      } finally {
        this.ignoreModal.loading = false;
      }
    },

    onDistrictDetail(item) {
      if (item.district) {
        this.filter.district = {
          label: item.district,
          value: item.district
        }
      }

      this.$router.push({
        path: '/dashboard/domain-detection/domain-distribution',
        query: {
          district: item.district,
          regency: item.regency,
          province: item.province,
        },
      })
    },
    onDetail(item) {
      this.$router.push({ name: 'ReviewDomainDetail', params: { domain_id: this.itemDomainId(item) } })
    },
    onViewAll() {
      this.$router.push({ name: 'ReviewDomain' })
    },
  },
};
</script>

<style scoped>
.page-title {
  font-size: 16px;
  font-weight: 700;
  color: #4b4b4b;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
  border-radius: 8px;
  box-shadow: 0 3px 2.5px rgba(0, 0, 0, 0.06);
}

.filter-bar__item {
  flex: 1;
  min-width: 0;
}

/* KPI card wrapper: ikon ⓘ + subteks */
.kpi-wrap {
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.kpi-wrap__info {
  position: absolute;
  top: 10px;
  right: 12px;
  padding: 0;
  border: none;
  background: transparent;
  font-size: 12px;
  line-height: 1;
  color: #9d9d9d;
  cursor: default;
}

.kpi-wrap__info:focus-visible {
  outline: 2px solid #267CE5;
  outline-offset: 2px;
  border-radius: 50%;
}

.kpi-wrap__sub {
  font-size: 11px;
  color: #9d9d9d;
  padding: 4px 8px 0;
}

.btn-download {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  background: white;
  border: none;
  border-radius: 4px;
  box-shadow: 0 0 1.5px rgba(0, 0, 0, 0.1);
  font-size: 12px;
  font-weight: 500;
  color: #4b4b4b;
  cursor: pointer;
  white-space: nowrap;
}

.btn-download:hover {
  background: #f5f5f5;
}

.btn-reset-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  height: 28px;
  background: #eaeaea;
  border: none;
  border-radius: 4px;
  box-shadow: 0 0 1.5px rgba(0, 0, 0, 0.1);
  font-size: 12px;
  font-weight: 500;
  color: #9d9d9d;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}

.btn-reset-filter:hover {
  background: #ddd;
}

.btn-reset-filter--active {
  background: white;
  color: #4b4b4b;
}

.btn-reset-filter--active:hover {
  background: #f5f5f5;
}

/* Watchlist / Success modals */
.watchlist-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px 16px 16px;
}

.watchlist-modal__icon {
  margin-bottom: 4px;
}

.watchlist-modal__body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: center;
  padding: 0 8px;
}

.watchlist-modal__title {
  font-size: 16px;
  font-weight: 600;
  color: #000;
  margin: 0;
}

.watchlist-modal__subtitle {
  font-size: 14px;
  color: rgba(165, 165, 165, 0.87);
  margin: 0;
}

.watchlist-modal__footer {
  display: flex;
  gap: 16px;
  width: 100%;
  padding: 10px 0 0;
}

.watchlist-modal__btn {
  flex: 1;
  padding: 10px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  text-align: center;
  white-space: nowrap;
}

.watchlist-modal__btn--cancel {
  background: #fff;
  border: 1px solid #267ce5;
  color: #267ce5;
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.1);
}

.watchlist-modal__btn--confirm {
  background: #267ce5;
  border: 1px solid #e8e8e8;
  color: #fff;
  box-shadow: 0 0 1.5px rgba(0, 0, 0, 0.1);
}

.watchlist-modal__btn--confirm:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
